import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import clientService from "./clientService";

export const getClients = createAsyncThunk(
  "clients/getClients",
  async (params = {}, thunkAPI) => {
    try {
      return await clientService.getClients(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const getClientById = createAsyncThunk(
  "clients/getClientById",
  async (id, thunkAPI) => {
    try {
      return await clientService.getClientById(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const createClient = createAsyncThunk(
  "clients/createClient",
  async (payload, thunkAPI) => {
    try {
      return await clientService.createClient(payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updateClient = createAsyncThunk(
  "clients/updateClient",
  async ({ id, data }, thunkAPI) => {
    try {
      return await clientService.updateClient({ id, data });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const archiveClient = createAsyncThunk(
  "clients/archiveClient",
  async (id, thunkAPI) => {
    try {
      return await clientService.archiveClient(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const restoreClient = createAsyncThunk(
  "clients/restoreClient",
  async (id, thunkAPI) => {
    try {
      return await clientService.restoreClient(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const deleteClient = createAsyncThunk(
  "clients/deleteClient",
  async (clientId, thunkAPI) => {
    try {
      return await clientService.deleteClient(clientId);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Cliënt verwijderen mislukt",
      );
    }
  },
);

const initialState = {
  clients: [],
  selectedClient: null,

  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  },

  filters: {
    search: "",
    is_active: "",
  },

  isLoading: false,
  isCreating: false,
  isUpdating: false,

  error: null,
};

const normalizeListPayload = (payload) => {
  const data = payload?.data;

  if (Array.isArray(data)) {
    return {
      items: data,
      pagination: initialState.pagination,
    };
  }

  return {
    items: data?.items || data?.clients || [],
    pagination: data?.pagination || initialState.pagination,
  };
};

const upsertClient = (clients, updatedClient) => {
  if (!updatedClient?._id) return clients;

  const exists = clients.some((client) => client._id === updatedClient._id);

  if (!exists) return [updatedClient, ...clients];

  return clients.map((client) =>
    client._id === updatedClient._id ? updatedClient : client,
  );
};

const clientSlice = createSlice({
  name: "clients",

  initialState,

  reducers: {
    clearClientError: (state) => {
      state.error = null;
    },

    setSelectedClient: (state, action) => {
      state.selectedClient = action.payload;
    },

    clearSelectedClient: (state) => {
      state.selectedClient = null;
    },

    setClientFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };

      state.pagination.page = 1;
    },

    setClientPage: (state, action) => {
      state.pagination.page = action.payload;
    },

    resetClientFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getClients.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getClients.fulfilled, (state, action) => {
        state.isLoading = false;

        const normalized = normalizeListPayload(action.payload);

        state.clients = normalized.items;
        state.pagination = normalized.pagination;
      })

      .addCase(getClients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Cliënten laden mislukt.";
      })

      .addCase(getClientById.fulfilled, (state, action) => {
        state.selectedClient = action.payload?.data || null;
      })

      .addCase(createClient.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })

      .addCase(createClient.fulfilled, (state, action) => {
        state.isCreating = false;

        const client = action.payload?.data;

        if (client) {
          state.clients = upsertClient(state.clients, client);
        }
      })

      .addCase(createClient.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || "Cliënt aanmaken mislukt.";
      })

      .addCase(updateClient.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })

      .addCase(updateClient.fulfilled, (state, action) => {
        state.isUpdating = false;

        const client = action.payload?.data;

        if (client) {
          state.clients = upsertClient(state.clients, client);
        }
      })

      .addCase(updateClient.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || "Cliënt wijzigen mislukt.";
      })

      .addCase(archiveClient.fulfilled, (state, action) => {
        const client = action.payload?.data;

        if (client) {
          state.clients = upsertClient(state.clients, client);
        }
      })

      .addCase(restoreClient.fulfilled, (state, action) => {
        const client = action.payload?.data;

        if (client) {
          state.clients = upsertClient(state.clients, client);
        }
      })
      .addCase(deleteClient.pending, (state) => {
        state.isDeleting = true;
      })

      .addCase(deleteClient.fulfilled, (state, action) => {
        state.isDeleting = false;

        const deletedId = action.payload?.data?.deleted_client_id;

        state.clients = state.clients.filter(
          (client) => client._id !== deletedId,
        );

        if (state.selectedClient?._id === deletedId) {
          state.selectedClient = null;
        }
      })

      .addCase(deleteClient.rejected, (state, action) => {
        state.isDeleting = false;

        state.error = action.payload;
      });
  },
});

export const {
  clearClientError,
  setSelectedClient,
  clearSelectedClient,
  setClientFilters,
  setClientPage,
  resetClientFilters,
} = clientSlice.actions;

export default clientSlice.reducer;
